/**
 * The pieces of the company record, shared by the side panel over a list
 * (prototype board 3) and the company page (board 4): the header, the tab bar,
 * and the four tab bodies -- Company, People we know, Sources, Lists.
 *
 * Blocks with no data hide instead of showing dashes: a thin PitchBook capture
 * reads well now and fills up later.
 */
import type {
  CompanyPeople,
  CompanyPlatformLink,
  CompanyRecord,
  KnownPerson,
  KnownPersonRole,
} from "@nu-assist/contracts";
import { Avatar, Spinner } from "@nu-assist/ui";
import { ExternalLink } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import { personPagePath, searchDetailPath } from "../../routing/paths";
import { linkedinHandle } from "../person-panel/personHandle";
import {
  formatCount,
  formatHq,
  formatUsdMillions,
  linkedinHref,
  pitchbookHref,
  websiteHref,
  websiteLabel,
} from "./format";
import { companyListPath } from "./paths";

export type CompanyTab = "company" | "people" | "sources" | "lists";

const PLATFORM_LABEL: Record<CompanyPlatformLink["platform"], string> = {
  pitchbook: "PitchBook",
  linkedin: "LinkedIn",
  thrive: "Thrive",
  hubspot: "HubSpot",
};

export function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function month(ym: string): string {
  const [y, m] = ym.split("-");
  const idx = Number(m) - 1;
  return MONTHS[idx] ? `${MONTHS[idx]} ${y}` : y;
}

/** "Mar 2021 → now" / "Jan 2016 → Jun 2019". */
export function rolePeriod(role: KnownPersonRole): string {
  return `${month(role.start_month)} → ${role.end_month ? month(role.end_month) : "now"}`;
}

// --- header -------------------------------------------------------------------

export function CompanyHeader({
  record,
  nameId,
  as = "h2",
  logoUrl,
}: {
  record: CompanyRecord;
  nameId?: string;
  as?: "h1" | "h2";
  /** Presigned URL of the stored logo; initials when absent. */
  logoUrl?: string | null;
}) {
  const Heading = as;
  const href = websiteHref(record.url);
  const linkedin = linkedinHref(record.linkedin_url);
  const pitchbook = record.platforms.find((p) => p.platform === "pitchbook");
  const pitchbookUrl = pitchbookHref(pitchbook?.platform_id);
  return (
    <header className="flex items-start gap-4">
      <Avatar name={record.name} src={logoUrl} shape="square" size="lg" />
      <div className="flex min-w-0 flex-col gap-1">
        <Heading id={nameId} className="text-h1 text-text">
          {record.name ?? "Unnamed company"}
        </Heading>
        {record.description && (
          <p className="line-clamp-2 text-body text-text-secondary">{record.description}</p>
        )}
        <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-label text-text-secondary">
          {href && (
            <a
              href={href}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 hover:text-accent-ink hover:underline"
            >
              {websiteLabel(record.url)}
              <ExternalLink className="h-3 w-3" aria-hidden="true" />
            </a>
          )}
          {linkedin && (
            <>
              <span aria-hidden="true">·</span>
              <a
                href={linkedin}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 hover:text-accent-ink hover:underline"
              >
                LinkedIn
                <ExternalLink className="h-3 w-3" aria-hidden="true" />
              </a>
            </>
          )}
          {pitchbook?.platform_id && (
            <>
              <span aria-hidden="true">·</span>
              {pitchbookUrl ? (
                <a
                  href={pitchbookUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 hover:text-accent-ink hover:underline"
                >
                  <img
                    src="/pitchbook.png"
                    alt=""
                    aria-hidden="true"
                    className="h-3 w-3 rounded-xs"
                  />
                  PitchBook {pitchbook.platform_id}
                  <ExternalLink className="h-3 w-3" aria-hidden="true" />
                </a>
              ) : (
                <span>PitchBook {pitchbook.platform_id}</span>
              )}
            </>
          )}
          <span aria-hidden="true">·</span>
          <span>captured {shortDate(record.created_at)}</span>
        </p>
      </div>
    </header>
  );
}

// --- tabs ---------------------------------------------------------------------

export function CompanyTabs({
  active,
  onSelect,
  counts,
  labels,
}: {
  active: CompanyTab;
  onSelect: (tab: CompanyTab) => void;
  counts: { people: number; sources: number; lists: number };
  /** The page says "Overview" / "People we know here" / "Lists & searches". */
  labels?: Partial<Record<CompanyTab, string>>;
}) {
  const tabs: Array<{ id: CompanyTab; label: string; count?: number }> = [
    { id: "company", label: labels?.company ?? "Company" },
    { id: "people", label: labels?.people ?? "People we know", count: counts.people },
    { id: "sources", label: labels?.sources ?? "Sources", count: counts.sources },
    { id: "lists", label: labels?.lists ?? "Lists", count: counts.lists },
  ];
  return (
    <div className="flex gap-1 border-b border-border" role="tablist" data-testid="company-tabs">
      {tabs.map((t) => (
        <button
          key={t.id}
          type="button"
          role="tab"
          aria-selected={active === t.id}
          data-testid={`company-tab-${t.id}`}
          onClick={() => onSelect(t.id)}
          className={`-mb-px border-b-2 px-3 py-2 text-body font-medium transition-colors ${
            active === t.id
              ? "border-accent text-accent"
              : "border-transparent text-text-secondary hover:border-border hover:text-text"
          }`}
        >
          {t.label}
          {t.count !== undefined && (
            <span className="ml-1.5 text-label text-text-muted">{t.count}</span>
          )}
        </button>
      ))}
    </div>
  );
}

// --- Company tab --------------------------------------------------------------

function Card({
  title,
  children,
  testId,
}: { title?: ReactNode; children: ReactNode; testId?: string }) {
  return (
    <section
      className="flex flex-col gap-2 rounded-lg border border-border bg-surface p-4"
      data-testid={testId}
    >
      {title && (
        <h3 className="text-label font-semibold uppercase tracking-wide text-text-muted">
          {title}
        </h3>
      )}
      {children}
    </section>
  );
}

function Big({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-caption text-text-muted">{label}</span>
      <span className="text-h2 font-semibold tabular-nums text-text">{value}</span>
    </div>
  );
}

export function CompanyOverview({
  record,
  people,
  onSeePeople,
  avatarUrls,
}: {
  record: CompanyRecord;
  people: CompanyPeople | undefined;
  onSeePeople: () => void;
  avatarUrls?: AvatarUrls;
}) {
  const big = (
    [
      ["Valuation", formatUsdMillions(record.valuation_usd_m)],
      ["Revenue", formatUsdMillions(record.revenue_usd_m)],
      ["Employees", formatCount(record.employees)],
      ["Founded", record.founded_year ? String(record.founded_year) : null],
    ] as Array<[string, string | null]>
  ).filter((b): b is [string, string] => b[1] !== null);
  const lastFinancing = formatUsdMillions(record.last_financing_size_usd_m);
  const financingLine = [
    lastFinancing ? `Last financing ${lastFinancing}` : null,
    record.last_deal_type,
    record.ownership_status,
  ]
    .filter(Boolean)
    .join(" · ");
  const industry = [record.sector, record.industry_group, record.industry_code]
    .filter(Boolean)
    .join(" · ");
  const hq = formatHq([record.hq_city, record.hq_state, record.hq_country]);
  const linked = record.platforms.filter((p) => p.linked);

  return (
    <div className="flex flex-col gap-3" data-testid="company-overview">
      {big.length > 0 && (
        <Card
          testId="company-financials"
          title={
            <span className="normal-case tracking-normal">
              <span className="text-text">Financials</span>{" "}
              <span className="font-normal">
                PitchBook, as captured {shortDate(record.updated_at)}
              </span>
            </span>
          }
        >
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            {big.map(([label, value]) => (
              <Big key={label} label={label} value={value} />
            ))}
          </div>
          {financingLine && <p className="text-label text-text-secondary">{financingLine}</p>}
        </Card>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {industry && (
          <Card title="Industry">
            <p className="text-body text-text">{industry}</p>
          </Card>
        )}
        {hq && (
          <Card title="Headquarters">
            <p className="text-body text-text">{hq}</p>
          </Card>
        )}
        {record.legal_name && (
          <Card title="Legal name">
            <p className="text-body text-text">{record.legal_name}</p>
          </Card>
        )}
        <Card title="Known through">
          <p className="flex flex-wrap gap-1.5">
            {record.platforms
              .filter((p) => p.platform === "pitchbook" || p.platform === "linkedin" || p.linked)
              .map((p) => (
                <span
                  key={p.platform}
                  className={`rounded-pill px-2 py-0.5 text-caption ${
                    p.linked
                      ? "bg-accent-muted text-accent-ink"
                      : "border border-dashed border-border text-text-muted"
                  }`}
                >
                  {PLATFORM_LABEL[p.platform]}
                  {p.linked ? "" : " —"}
                </span>
              ))}
            {linked.length === 0 && (
              <span className="text-caption text-text-muted">No platform yet</span>
            )}
          </p>
        </Card>
      </div>

      <Card title="People we know here">
        <PeoplePreview people={people} onSeeAll={onSeePeople} avatarUrls={avatarUrls} />
      </Card>
    </div>
  );
}

function PeoplePreview({
  people,
  onSeeAll,
  avatarUrls,
}: {
  people: CompanyPeople | undefined;
  onSeeAll: () => void;
  avatarUrls?: AvatarUrls;
}) {
  if (!people) return <Spinner size="sm" label="Loading people…" />;
  if (people.items.length === 0) return <NobodyYet people={people} />;
  const shown = people.items.slice(0, 3);
  return (
    <div className="flex flex-col gap-2">
      {shown.map((p) => (
        <PersonLine key={p.linkedin_url_canonical} person={p} avatarUrls={avatarUrls} />
      ))}
      {people.items.length > shown.length && (
        <button
          type="button"
          onClick={onSeeAll}
          className="self-start text-label text-accent-ink hover:underline"
        >
          See all {people.items.length}
        </button>
      )}
    </div>
  );
}

function NobodyYet({ people }: { people: CompanyPeople }) {
  return (
    <div
      className="rounded-md border border-dashed border-border px-4 py-5 text-center"
      data-testid="people-empty"
    >
      <p className="text-body text-text">Nobody yet</p>
      <p className="text-caption text-text-muted">
        {people.linked_to_linkedin
          ? "No contact in our lists has worked here."
          : "People are matched by the company's LinkedIn page, which this company isn't linked to yet."}
      </p>
    </div>
  );
}

function PersonName({ person }: { person: KnownPerson }) {
  const handle = linkedinHandle(person.linkedin_url_canonical);
  const name = person.name ?? "Unnamed contact";
  return handle ? (
    <Link
      to={personPagePath(handle)}
      className="font-medium text-text hover:text-accent-ink hover:underline"
    >
      {name}
    </Link>
  ) : (
    <span className="font-medium text-text">{name}</span>
  );
}

/** Stored avatar key → presigned URL, from `useAvatarUrls` (initials when absent). */
type AvatarUrls = ReadonlyMap<string, string> | undefined;

function avatarOf(person: KnownPerson, avatarUrls: AvatarUrls): string | undefined {
  return person.profile_image_s3_key ? avatarUrls?.get(person.profile_image_s3_key) : undefined;
}

function PersonLine({ person, avatarUrls }: { person: KnownPerson; avatarUrls?: AvatarUrls }) {
  const role = person.roles[0];
  return (
    <div className="flex items-center gap-3 text-body">
      <Avatar name={person.name} src={avatarOf(person, avatarUrls)} size="sm" />
      <PersonName person={person} />
      <span className="truncate text-text-secondary">
        {role?.title ?? ""}
        {role ? ` · ${rolePeriod(role)}` : ""}
      </span>
    </div>
  );
}

// --- People tab ---------------------------------------------------------------

export function PeopleWeKnow({
  people,
  avatarUrls,
}: {
  people: CompanyPeople | undefined;
  avatarUrls?: AvatarUrls;
}) {
  if (!people) return <Spinner label="Loading people…" />;
  return (
    <div className="flex flex-col gap-3" data-testid="company-people">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-label text-text-secondary">
          Everyone in our lists whose work history names this company
        </span>
        <span className="flex-1" />
        <span className="rounded-pill bg-accent-muted px-2.5 py-0.5 text-caption text-accent-ink">
          Currently here {people.current}
        </span>
        <span className="rounded-pill bg-surface-raised px-2.5 py-0.5 text-caption text-text-secondary">
          Alumni {people.alumni}
        </span>
      </div>
      {people.items.length === 0 ? (
        <NobodyYet people={people} />
      ) : (
        <div className="overflow-x-auto rounded-md border border-border bg-table-surface">
          <table className="w-full border-collapse" data-testid="company-people-table">
            <thead>
              <tr className="border-b border-border text-left text-label font-semibold text-text-secondary">
                <th className="px-3 py-2">Person</th>
                <th className="px-3 py-2">Role here</th>
                <th className="px-3 py-2">When</th>
                <th className="px-3 py-2">Where we met them</th>
              </tr>
            </thead>
            <tbody>
              {people.items.map((p) => {
                const role = p.roles[0];
                return (
                  <tr
                    key={p.linkedin_url_canonical}
                    className="border-b border-border last:border-b-0"
                  >
                    <td className="px-3 py-2 text-body">
                      <span className="flex items-center gap-2">
                        <Avatar name={p.name} src={avatarOf(p, avatarUrls)} size="sm" />
                        <PersonName person={p} />
                      </span>
                    </td>
                    <td className="px-3 py-2 text-body text-text-secondary">
                      {role?.title ?? "—"}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-body text-text-secondary">
                      {role ? rolePeriod(role) : "—"}
                      {!p.current && " · alumni"}
                    </td>
                    <td className="px-3 py-2 text-label text-text-secondary">
                      {p.met_in.length === 0
                        ? "—"
                        : p.met_in.map((m, i) => (
                            <span key={m.import_id}>
                              {i > 0 && ", "}
                              <Link
                                to={`/research/imports/${m.import_id}`}
                                className="hover:text-accent-ink hover:underline"
                              >
                                {m.name}
                              </Link>
                              {m.search_id_thrive ? ` · search ${m.search_id_thrive}` : ""}
                            </span>
                          ))}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// --- Sources tab --------------------------------------------------------------

export function CompanySources({
  record,
  people,
}: { record: CompanyRecord; people: CompanyPeople | undefined }) {
  const histories = people ? people.items.length : null;
  return (
    <div className="flex flex-col gap-2" data-testid="company-sources">
      <p className="text-label text-text-secondary">
        One record, many platforms: each is a link on this company, not a copy of it.
      </p>
      <ul className="flex flex-col divide-y divide-border rounded-md border border-border bg-surface">
        {record.platforms.map((p) => (
          <li key={p.platform} className="flex items-center gap-3 px-4 py-3 text-body">
            <span
              aria-hidden="true"
              className={`h-2 w-2 shrink-0 rounded-full ${
                p.linked ? "bg-accent" : "border border-dashed border-text-muted"
              }`}
            />
            <span className={`flex-1 ${p.linked ? "text-text" : "text-text-muted"}`}>
              {PLATFORM_LABEL[p.platform]}
              {p.linked ? ` · ${p.platform_id}` : " · not linked"}
            </span>
            <span className="text-caption text-text-muted">
              {p.linked
                ? p.platform === "pitchbook"
                  ? `captured ${shortDate(record.updated_at)}`
                  : "linked"
                : p.platform === "thrive" || p.platform === "hubspot"
                  ? "phase 2"
                  : ""}
            </span>
          </li>
        ))}
        {histories !== null && histories > 0 && (
          <li className="flex items-center gap-3 px-4 py-3 text-body">
            <span aria-hidden="true" className="h-2 w-2 shrink-0 rounded-full bg-success" />
            <span className="flex-1 text-text">
              {histories} work {histories === 1 ? "history" : "histories"} of our contacts
            </span>
            <span className="text-caption text-text-muted">live</span>
          </li>
        )}
      </ul>
    </div>
  );
}

/** Platforms linked + the live work-history source: the Sources tab's count. */
export function sourceCount(record: CompanyRecord, people: CompanyPeople | undefined): number {
  return (
    record.platforms.filter((p) => p.linked).length + (people && people.items.length > 0 ? 1 : 0)
  );
}

// --- Lists tab ----------------------------------------------------------------

export function CompanyListsOn({
  record,
  currentListId,
}: { record: CompanyRecord; currentListId?: string }) {
  if (record.lists.length === 0) {
    return <p className="text-body text-text-secondary">This company is on no company list.</p>;
  }
  return (
    <ul
      className="flex flex-col divide-y divide-border rounded-md border border-border bg-surface"
      data-testid="company-lists-on"
    >
      {record.lists.map((l) => (
        <li
          key={l.list_id}
          className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3 text-body"
        >
          <Link
            to={companyListPath(l.list_id)}
            className="font-medium text-text hover:text-accent-ink hover:underline"
          >
            {l.name}
          </Link>
          {l.list_id === currentListId && (
            <span className="rounded-pill bg-accent-muted px-2 py-0.5 text-caption text-accent-ink">
              this list
            </span>
          )}
          <span className="flex-1" />
          {l.search_id_thrive && (
            <Link
              to={searchDetailPath(l.search_id_thrive)}
              className="text-label text-text-secondary hover:text-accent-ink hover:underline"
            >
              {l.search_name ?? `Search ${l.search_id_thrive}`}
            </Link>
          )}
          <span className="text-caption text-text-muted">added {shortDate(l.added_at)}</span>
        </li>
      ))}
    </ul>
  );
}
